import React, { useEffect, useRef, useState } from "react";
import Modal from "../../../components/ui/Modal";
import { Button } from "../../../components/ui/button";
import BatchFormFields from "./BatchFormFields";
import { useApiMutation } from "../../../hooks/useApiMutation";
import { batchApi } from "../../../api/batchApi";
import { useToast } from "../../../components/ui/toast/ToastProvider";
import useBatchFormOptions from "./useBatchFormOptions";
import { changeBatchField, resolveBatchInput, buildBatchPayload, toBatchFormValues, unwrapBatch, validateBatchValues } from "./batchFormUtils";

const EditBatchModal = ({ open, batch, onClose, onUpdated, rawMaterialOptions, partOptions }) => {
  const toast = useToast();
  const [values, setValues] = useState(() => toBatchFormValues(batch));
  const [errors, setErrors] = useState({});
  const submitting = useRef(false);
  const options = useBatchFormOptions(open, batch?.plant_id, partOptions, rawMaterialOptions, values.raw_material);
  const resolvedValues = resolveBatchInput(values, options.inputRecords, options.partOptions, options.materialOptions);
  const { run: updateBatch, isLoading, error, reset } = useApiMutation((payload) =>
    batchApi.update(batch?.uid || batch?.id, payload)
  );
  useEffect(() => {
    setValues(toBatchFormValues(batch));
    setErrors({});
    reset();
  }, [batch, reset]);

  const handleChange = (name, value) => {
    setValues((current) => changeBatchField(current, name, value));
    setErrors({});
  };
  const handleClose = () => { if (!submitting.current) onClose(); };
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting.current || options.isLoading || options.isLoadingInputs || options.error) return;
    const validation = validateBatchValues(resolvedValues, options.partOptions, options.materialOptions, options.inputRecords, batch);
    setErrors(validation);
    if (Object.keys(validation).length) return;
    submitting.current = true;
    try {
      const updated = unwrapBatch(await updateBatch(buildBatchPayload(resolvedValues, options.partOptions)));
      toast.success("Batch updated successfully.");
      onUpdated?.(updated);
      onClose();
    } catch (err) {
      toast.error(err?.message || "Unable to update batch.");
    } finally {
      submitting.current = false;
    }
  };
  if (!batch) return null;
  return (
    <Modal open={open} onClose={handleClose} title={`Edit Batch — ${batch.batch_id || batch.uid || batch.id}`} size="lg">
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <BatchFormFields values={resolvedValues} onChange={handleChange} rawMaterialOptions={options.materialOptions}
          partOptions={options.partOptions} inputRecords={options.inputRecords} loadingInputs={options.isLoadingInputs} existingBatch={batch} errors={errors} loading={options.isLoading} disabled={isLoading} />
        {options.error && <p role="alert" className="text-sm text-rose-600">Unable to load materials, input batches or linked items. <button type="button" onClick={options.refetch} className="underline">Retry</button></p>}
        {error && <p role="alert" className="text-sm text-rose-600">{error.message}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>Cancel</Button>
          <Button type="submit" disabled={isLoading || options.isLoading || options.isLoadingInputs || Boolean(options.error)}>{isLoading ? "Saving…" : "Save Changes"}</Button>
        </div>
      </form>
    </Modal>
  );
};

export default EditBatchModal;
